/**
 * Sello visible estilo Adobe Acrobat (capa n2 / Standard Text).
 * Referencia: iText PdfSignatureAppearance — "Digitally signed by {CN}\nDate: yyyy.MM.dd HH:mm:ss z"
 * Adobe DevNet: https://www.adobe.com/devnet-docs/acrobatetk/tools/DigSigDC/appearances.html
 */

/** Píxeles por punto PDF al rasterizar (~576 DPI). */
const PIXELS_PER_POINT = 8;

export const MIN_STAMP_PIXEL_WIDTH = 1200;
export const MIN_STAMP_PIXEL_HEIGHT = 240;

const STAMP_LABEL_PREFIX = 'Firmado digitalmente por ';
const STAMP_DATE_PREFIX = 'Fecha: ';
const STAMP_PAD_X_PT = 2;
const STAMP_PAD_Y_PT = 1.5;
const STAMP_FONT_FAMILY = 'Helvetica, Arial, sans-serif';
const STAMP_BG_COLOR = '#ffffff';
const CENTER_LOGO_SRC = 'assets/icons/logo_certicamara.png';
const COL_GAP_PT = 2.5;
const COL_LEFT_RATIO = 0.30;
const COL_CENTER_RATIO = 0.22;
const CENTER_ICON_OPACITY = 0.22;

/** Mínimo más ancho para layout tipo Adobe (CN | marca | metadatos). */
export const MIN_SIGNATURE_ZONE_WIDTH_PT = 130;
export const MIN_SIGNATURE_ZONE_HEIGHT_PT = 28;

let centerLogoPromise: Promise<HTMLImageElement | null> | null = null;

export interface AdobeStampSizePt {
  widthPt: number;
  heightPt: number;
}

export function isSignatureZoneLargeEnough(
  widthPt: number,
  heightPt: number,
  signerCn?: string
): boolean {
  const min = signerCn ? getSignatureZoneMinimum(signerCn) : {
    widthPt: MIN_SIGNATURE_ZONE_WIDTH_PT,
    heightPt: MIN_SIGNATURE_ZONE_HEIGHT_PT
  };
  return widthPt >= min.widthPt && heightPt >= min.heightPt;
}

export function getSignatureZoneMinimum(signerCn: string): AdobeStampSizePt {
  const cn = signerCn.trim() || 'Firmante';
  const dateLabel = formatAdobeStampDate(new Date());
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { widthPt: MIN_SIGNATURE_ZONE_WIDTH_PT, heightPt: MIN_SIGNATURE_ZONE_HEIGHT_PT };
  }

  for (let widthPt = MIN_SIGNATURE_ZONE_WIDTH_PT; widthPt <= 320; widthPt += 2) {
    for (let heightPt = MIN_SIGNATURE_ZONE_HEIGHT_PT; heightPt <= 80; heightPt += 1) {
      if (computeThreeColumnStampLayout(ctx, widthPt, heightPt, cn, dateLabel, STAMP_FONT_FAMILY)) {
        return { widthPt: Math.ceil(widthPt), heightPt: Math.ceil(heightPt) };
      }
    }
  }

  return { widthPt: 160, heightPt: 36 };
}

function pad2(n: number): string {
  return `${n}`.padStart(2, '0');
}

/**
 * Formato visual Adobe (iText SimpleDateFormat "yyyy.MM.dd HH:mm:ss z").
 * Ejemplo: 2026.06.12 17:38:18 GMT-05:00
 */
export function formatAdobeStampDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const min = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offH = pad2(Math.floor(abs / 60));
  const offM = pad2(abs % 60);
  return `${yyyy}.${mm}.${dd} ${hh}:${min}:${ss} GMT${sign}${offH}:${offM}`;
}

/** Texto completo del bloque descriptivo Adobe (como layer n2). */
export function buildAdobeStampDescription(signerCn: string, date: Date = new Date()): string {
  const cn = signerCn.trim() || 'Firmante';
  return `${STAMP_LABEL_PREFIX}${cn}\n${STAMP_DATE_PREFIX}${formatAdobeStampDate(date)}`;
}

function normalizeZoneSizePt(sizePt?: AdobeStampSizePt): AdobeStampSizePt {
  return {
    widthPt: Math.max(sizePt?.widthPt ?? MIN_SIGNATURE_ZONE_WIDTH_PT, 1),
    heightPt: Math.max(sizePt?.heightPt ?? MIN_SIGNATURE_ZONE_HEIGHT_PT, 1)
  };
}

function resolveRenderDimensions(zoneSizePt: AdobeStampSizePt): {
  renderWidthPt: number;
  renderHeightPt: number;
  pixelWidth: number;
  pixelHeight: number;
} {
  const pixelWidth = Math.max(Math.round(zoneSizePt.widthPt * PIXELS_PER_POINT), MIN_STAMP_PIXEL_WIDTH);
  const pixelHeight = Math.max(Math.round(zoneSizePt.heightPt * PIXELS_PER_POINT), MIN_STAMP_PIXEL_HEIGHT);
  return {
    renderWidthPt: pixelWidth / PIXELS_PER_POINT,
    renderHeightPt: pixelHeight / PIXELS_PER_POINT,
    pixelWidth,
    pixelHeight
  };
}

function wrapTextToWidthAll(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines: string[] = [];
  let current = words[0];

  for (let i = 1; i < words.length; i++) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
}

/** Salto de línea de fecha estilo Adobe: primero entre fecha y hora, no partir HH:MM:SS. */
function wrapAdobeDateLine(ctx: CanvasRenderingContext2D, dateLine: string, maxWidth: number): string[] {
  if (ctx.measureText(dateLine).width <= maxWidth) return [dateLine];

  const match = dateLine.match(/^(Fecha:\s*)(\d{4}\.\d{2}\.\d{2})\s+(.+)$/);
  if (match) {
    const line1 = `${match[1]}${match[2]}`;
    if (ctx.measureText(line1).width <= maxWidth) {
      const rest = wrapTextToWidthAll(ctx, match[3], maxWidth);
      return rest.length ? [line1, ...rest] : [line1];
    }
  }

  return wrapTextToWidthAll(ctx, dateLine, maxWidth);
}

function buildAdobeDescriptionLines(
  ctx: CanvasRenderingContext2D,
  cn: string,
  dateLabel: string,
  contentW: number,
  fontSize: number,
  family: string
): string[] {
  ctx.font = `400 ${fontSize}px ${family}`;
  const signedBlock = `${STAMP_LABEL_PREFIX}${cn}`;
  const signedLines = wrapTextToWidthAll(ctx, signedBlock, contentW);
  const dateLines = wrapAdobeDateLine(ctx, `${STAMP_DATE_PREFIX}${dateLabel}`, contentW);
  return [...signedLines, ...dateLines];
}

function canvasToBase64(canvas: HTMLCanvasElement): string {
  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
}

function createHiResCanvas(
  pixelWidth: number,
  pixelHeight: number,
  renderWidthPt: number,
  renderHeightPt: number,
  transparent = false
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, pixelWidth);
  canvas.height = Math.max(1, pixelHeight);
  const ctx = canvas.getContext('2d', { alpha: transparent });
  if (!ctx) return null;

  const scaleX = pixelWidth / renderWidthPt;
  const scaleY = pixelHeight / renderHeightPt;
  ctx.scale(scaleX, scaleY);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (transparent) {
    ctx.clearRect(0, 0, renderWidthPt, renderHeightPt);
  }

  return { canvas, ctx };
}

interface ThreeColumnStampLayout {
  padX: number;
  padY: number;
  leftColW: number;
  centerColW: number;
  rightColW: number;
  leftX: number;
  centerX: number;
  rightX: number;
  cnFontSize: number;
  cnLineHeight: number;
  cnLines: string[];
  cnBlockH: number;
  metaFontSize: number;
  metaLineHeight: number;
  metaLines: string[];
  metaBlockH: number;
  centerIconSize: number;
  centerBrandSize: number;
  centerBlockH: number;
  contentH: number;
}

function getColumnWidths(innerW: number): { leftColW: number; centerColW: number; rightColW: number } {
  const leftColW = innerW * COL_LEFT_RATIO;
  const centerColW = innerW * COL_CENTER_RATIO;
  const rightColW = Math.max(innerW - leftColW - centerColW - COL_GAP_PT * 2, 1);
  return { leftColW, centerColW, rightColW };
}

function wrapCnLines(
  ctx: CanvasRenderingContext2D,
  cn: string,
  maxWidth: number,
  fontSize: number,
  family: string
): string[] {
  ctx.font = `700 ${fontSize}px ${family}`;
  return wrapTextToWidthAll(ctx, cn, maxWidth);
}

function computeThreeColumnStampLayout(
  ctx: CanvasRenderingContext2D,
  widthPt: number,
  heightPt: number,
  cn: string,
  dateLabel: string,
  family: string
): ThreeColumnStampLayout | null {
  const padX = STAMP_PAD_X_PT;
  const padY = STAMP_PAD_Y_PT;
  const innerW = Math.max(widthPt - padX * 2, 1);
  const innerH = Math.max(heightPt - padY * 2, 1);
  const { leftColW, centerColW, rightColW } = getColumnWidths(innerW);
  const leftX = padX;
  const centerX = padX + leftColW + COL_GAP_PT;
  const rightX = centerX + centerColW + COL_GAP_PT;

  const maxCnFont = Math.min(innerH * 0.34, 13);
  const minCnFont = 6.5;

  for (let cnFontSize = maxCnFont; cnFontSize >= minCnFont; cnFontSize -= 0.2) {
    const cnLineHeight = cnFontSize * 1.06;
    const cnLines = wrapCnLines(ctx, cn, leftColW, cnFontSize, family);
    const cnBlockH = cnLines.length * cnLineHeight;

    const maxMetaFont = Math.min(cnFontSize * 0.52, 8.5);
    for (let metaFontSize = maxMetaFont; metaFontSize >= 5; metaFontSize -= 0.15) {
      const metaLineHeight = metaFontSize * 1.12;
      const metaLines = buildAdobeDescriptionLines(ctx, cn, dateLabel, rightColW, metaFontSize, family);
      const metaBlockH = metaLines.length * metaLineHeight;

      const lateralBlockH = Math.max(cnBlockH, metaBlockH);
      const centerBlockH = 0;
      const centerIconSize = 0;
      const centerBrandSize = 0;
      const totalBlockH = lateralBlockH;

      if (totalBlockH <= innerH) {
        return {
          padX,
          padY,
          leftColW,
          centerColW,
          rightColW,
          leftX,
          centerX,
          rightX,
          cnFontSize,
          cnLineHeight,
          cnLines,
          cnBlockH,
          metaFontSize,
          metaLineHeight,
          metaLines,
          metaBlockH,
          centerIconSize,
          centerBrandSize,
          centerBlockH,
          contentH: innerH
        };
      }
    }
  }

  return null;
}

function loadStampImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function loadCenterBrandLogo(): Promise<HTMLImageElement | null> {
  if (!centerLogoPromise) {
    centerLogoPromise = loadStampImage(CENTER_LOGO_SRC).then(async logo => {
      if (!logo) return null;
      return stripDarkBackground(logo);
    });
  }
  return centerLogoPromise;
}

/** Elimina fondo oscuro del PNG para usarlo como marca de agua transparente. */
function stripDarkBackground(source: HTMLImageElement): Promise<HTMLImageElement | null> {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  if (!width || !height) return Promise.resolve(source);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(source);

  ctx.drawImage(source, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    if (r < 45 && g < 45 && b < 45) {
      pixels[i + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const processed = new Image();
  return new Promise(resolve => {
    processed.onload = () => resolve(processed);
    processed.onerror = () => resolve(source);
    processed.src = canvas.toDataURL('image/png');
  });
}

async function resolveCenterBrandLogo(): Promise<HTMLImageElement | null> {
  const logo = await loadCenterBrandLogo();
  if (!logo) return null;
  if (logo.complete && logo.naturalWidth > 0) return logo;

  return new Promise(resolve => {
    logo.onload = () => resolve(logo);
    logo.onerror = () => resolve(null);
  });
}

function drawFullWidthBrandWatermark(
  ctx: CanvasRenderingContext2D,
  widthPt: number,
  heightPt: number,
  logo: HTMLImageElement
): void {
  const logoW = logo.naturalWidth || logo.width;
  const logoH = logo.naturalHeight || logo.height;
  if (!logoW || !logoH) return;

  const maxW = widthPt - STAMP_PAD_X_PT;
  const maxH = heightPt - STAMP_PAD_Y_PT * 2;
  const widthScale = maxW / logoW;
  let drawW = maxW;
  let drawH = logoH * widthScale;
  if (drawH > maxH) {
    const fitScale = Math.min(maxW / logoW, maxH / logoH);
    drawW = logoW * fitScale;
    drawH = logoH * fitScale;
  }
  const drawX = (widthPt - drawW) / 2;
  const drawY = (heightPt - drawH) / 2;

  ctx.save();
  ctx.globalAlpha = CENTER_ICON_OPACITY;
  ctx.drawImage(logo, drawX, drawY, drawW, drawH);
  ctx.restore();
}

function drawStampBackground(ctx: CanvasRenderingContext2D, widthPt: number, heightPt: number): void {
  ctx.fillStyle = STAMP_BG_COLOR;
  ctx.fillRect(0, 0, widthPt, heightPt);
}

function drawThreeColumnStamp(
  ctx: CanvasRenderingContext2D,
  layout: ThreeColumnStampLayout,
  centerLogo: HTMLImageElement | null,
  family: string,
  widthPt: number,
  heightPt: number
): void {
  if (centerLogo) {
    drawFullWidthBrandWatermark(ctx, widthPt, heightPt, centerLogo);
  }

  const totalBlockH = Math.max(layout.cnBlockH, layout.metaBlockH);
  const blockTop = layout.padY + Math.max(0, (layout.contentH - totalBlockH) / 2);

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = `700 ${layout.cnFontSize}px ${family}`;
  let cnY = blockTop + (totalBlockH - layout.cnBlockH) / 2;
  layout.cnLines.forEach(line => {
    ctx.fillText(line, layout.leftX, cnY);
    cnY += layout.cnLineHeight;
  });

  ctx.font = `400 ${layout.metaFontSize}px ${family}`;
  let metaY = blockTop + (totalBlockH - layout.metaBlockH) / 2;
  layout.metaLines.forEach(line => {
    ctx.fillText(line, layout.rightX, metaY);
    metaY += layout.metaLineHeight;
  });
}

export async function buildAdobeStyleStampImageBase64(
  signerCn: string,
  date: Date = new Date(),
  sizePt?: AdobeStampSizePt
): Promise<string> {
  const zoneSize = normalizeZoneSizePt(sizePt);
  const { renderWidthPt, renderHeightPt, pixelWidth, pixelHeight } = resolveRenderDimensions(zoneSize);
  const surface = createHiResCanvas(pixelWidth, pixelHeight, renderWidthPt, renderHeightPt, false);
  if (!surface) return '';

  const { canvas, ctx } = surface;
  const cn = signerCn.trim() || 'Firmante';
  const dateLabel = formatAdobeStampDate(date);

  drawStampBackground(ctx, renderWidthPt, renderHeightPt);

  const layout = computeThreeColumnStampLayout(ctx, renderWidthPt, renderHeightPt, cn, dateLabel, STAMP_FONT_FAMILY);
  if (!layout) return '';

  const centerLogo = await resolveCenterBrandLogo();
  drawThreeColumnStamp(ctx, layout, centerLogo, STAMP_FONT_FAMILY, renderWidthPt, renderHeightPt);
  return canvasToBase64(canvas);
}

/**
 * Solo marca de agua Certicámara (sin CN).
 * Para RSE: el nombre real lo inyecta TrustedX con signature_details.type = "name".
 */
export async function buildBrandWatermarkImageBase64(
  sizePt?: AdobeStampSizePt
): Promise<string> {
  const zoneSize = normalizeZoneSizePt(sizePt);
  const { renderWidthPt, renderHeightPt, pixelWidth, pixelHeight } = resolveRenderDimensions(zoneSize);
  const surface = createHiResCanvas(pixelWidth, pixelHeight, renderWidthPt, renderHeightPt, false);
  if (!surface) return '';

  const { canvas, ctx } = surface;
  drawStampBackground(ctx, renderWidthPt, renderHeightPt);
  const centerLogo = await resolveCenterBrandLogo();
  if (centerLogo) {
    drawFullWidthBrandWatermark(ctx, renderWidthPt, renderHeightPt, centerLogo);
  }
  return canvasToBase64(canvas);
}

export async function buildUploadStampImageBase64(
  uploadedBase64: string,
  date: Date,
  sizePt?: AdobeStampSizePt,
  loadImage: (b64: string) => Promise<HTMLImageElement | null> = defaultLoadImage
): Promise<string> {
  const zoneSize = normalizeZoneSizePt(sizePt);
  const { renderWidthPt, renderHeightPt, pixelWidth, pixelHeight } = resolveRenderDimensions(zoneSize);
  const surface = createHiResCanvas(pixelWidth, pixelHeight, renderWidthPt, renderHeightPt, false);
  if (!surface) return '';

  const { canvas, ctx } = surface;
  const img = await loadImage(uploadedBase64);
  if (!img) return '';

  const pad = STAMP_PAD_X_PT;
  const dateLabel = formatAdobeStampDate(date);
  const contentW = renderWidthPt - pad * 2;

  drawStampBackground(ctx, renderWidthPt, renderHeightPt);

  const imageAreaH = renderHeightPt * 0.62;
  const imageAreaW = contentW;
  const scale = Math.min(imageAreaW / img.width, imageAreaH / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const drawX = pad + Math.max(0, (imageAreaW - drawW) / 2);
  const drawY = pad;
  ctx.drawImage(img, drawX, drawY, drawW, drawH);

  let dateSize = Math.min(renderHeightPt * 0.12, 9);
  ctx.font = `400 ${dateSize}px ${STAMP_FONT_FAMILY}`;
  let dateLines = wrapAdobeDateLine(ctx, `${STAMP_DATE_PREFIX}${dateLabel}`, contentW);
  while (dateSize > 5 && dateLines.length * dateSize * 1.12 > renderHeightPt * 0.28) {
    dateSize -= 0.3;
    ctx.font = `400 ${dateSize}px ${STAMP_FONT_FAMILY}`;
    dateLines = wrapAdobeDateLine(ctx, `${STAMP_DATE_PREFIX}${dateLabel}`, contentW);
  }

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let y = pad + imageAreaH + pad * 0.4;
  dateLines.forEach(line => {
    ctx.fillText(line, pad, y);
    y += dateSize * 1.12;
  });

  return canvasToBase64(canvas);
}

function defaultLoadImage(base64: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    const mimeType = base64.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
    img.src = `data:${mimeType};base64,${base64}`;
  });
}
